import { cn } from '../../utils/cn';
import type { LeafRanges, SampleType, SoilDerived, SoilResult } from '../../types/soil';
import {
  ANALYTE_BY_KEY, DERIVED_GUIDE, GROUPS, GROUP_LABEL, LEAF_NOT_ADVICE, NOT_ADVICE, OTHER, fmtNum, guideVerdict,
  resultLabel, showValue, type GuideVerdict,
} from '../../utils/soilAnalytes';

const VERDICT_CLS: Record<Exclude<GuideVerdict, null>, string> = {
  low: 'bg-amber-50 text-amber-800 border-amber-200',
  high: 'bg-amber-50 text-amber-800 border-amber-200',
  ok: 'bg-green-50 text-green-700 border-green-200',
};
const VERDICT_WORD: Record<Exclude<GuideVerdict, null>, string> = { low: 'Below', high: 'Above', ok: 'Within' };

function VerdictChip({ v, against }: { v: GuideVerdict; against: string }) {
  if (!v) return null;
  return (
    <span className={cn('text-[11px] px-1.5 py-0.5 rounded border whitespace-nowrap', VERDICT_CLS[v])}>
      {VERDICT_WORD[v]} {against}
    </span>
  );
}

const rangeText = (r: { low?: number | null; high?: number | null }) =>
  r.low != null && r.high != null ? `${fmtNum(r.low)}–${fmtNum(r.high)}`
    : r.low != null ? `≥ ${fmtNum(r.low)}` : r.high != null ? `≤ ${fmtNum(r.high)}` : null;

function Change({ now, before }: { now: number; before: number | null | undefined }) {
  if (before == null || !Number.isFinite(before)) return null;
  const d = now - before;
  if (Math.abs(d) < 1e-9) return <span className="text-neutral-500">no change</span>;
  return <span className="text-neutral-500">{d > 0 ? '+' : '−'}{fmtNum(Math.abs(d))}</span>;
}

/** A standard as a guide: null ends are open. */
function standardGuide(r: { low: number | null; high: number | null } | undefined) {
  if (!r) return undefined;
  return { low: r.low ?? undefined, high: r.high ?? undefined };
}

interface Row {
  key: string;
  label: string;
  value: string;
  unit: string | null;
  numeric: number;
  before: number | null;
  beforeText: string | null;
  verdict: GuideVerdict;
  range?: string | null;
  method: string | null;
}

function Rows({ rows, showPrevious, against }: { rows: Row[]; showPrevious: boolean; against: string }) {
  return (
    <ul className="divide-y divide-neutral-100">
      {rows.map((r) => (
        <li key={r.key} className="py-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <div className="min-w-0 flex-1 basis-40">
            <p className="text-sm text-neutral-800 break-words">{r.label}</p>
            {r.method && <p className="text-xs text-neutral-500 break-words">{r.method}</p>}
          </div>
          <div className="flex flex-wrap items-baseline justify-end gap-x-2 gap-y-1 text-sm">
            <span className="font-semibold text-neutral-900 tabular-nums">{r.value}</span>
            {r.unit && <span className="text-xs text-neutral-500">{r.unit}</span>}
            {showPrevious && r.beforeText && (
              <span className="text-xs text-neutral-500 tabular-nums">
                was {r.beforeText} · <Change now={r.numeric} before={r.before} />
              </span>
            )}
            {r.range && <span className="text-xs text-neutral-500 whitespace-nowrap">({r.range})</span>}
            <VerdictChip v={r.verdict} against={against} />
          </div>
        </li>
      ))}
    </ul>
  );
}

/**
 * A sample's results grouped as a lab report groups them, with the figures
 * worked out from them and, when given, the previous sample of the same place
 * beside each. A soil sample is marked against broad guides; a leaf sample
 * against its crop's standards, when the organisation has set them.
 */
export default function SoilResultsView({
  results, derived, previous, type = 'soil', standards,
}: {
  results: SoilResult[];
  derived: SoilDerived;
  previous?: { results: SoilResult[]; derived: SoilDerived } | null;
  type?: SampleType;
  standards?: LeafRanges | null;
}) {
  const leaf = type === 'leaf';
  const against = leaf ? 'standard' : 'guide';
  const prevBy = new Map((previous?.results ?? []).map((r) => [r.analyte === OTHER ? `o:${r.label}` : r.analyte, r]));
  const toRow = (r: SoilResult): Row => {
    const k = r.analyte === OTHER ? `o:${r.label}` : r.analyte;
    const p = prevBy.get(k);
    const a = ANALYTE_BY_KEY.get(r.analyte);
    return {
      key: k,
      label: resultLabel(r),
      value: showValue(r),
      unit: r.unit || a?.unit || null,
      numeric: r.value,
      before: p ? p.value : null,
      beforeText: p ? showValue(p) : null,
      verdict: guideVerdict(leaf ? standardGuide(standards?.[r.analyte]) : a?.guide, r.value),
      range: leaf && standards?.[r.analyte] ? rangeText(standards[r.analyte]) : null,
      method: r.method,
    };
  };
  const groups = GROUPS[type]
    .map((g) => ({ g, rows: results.filter((r) => ANALYTE_BY_KEY.get(r.analyte)?.group === g).map(toRow) }))
    .filter((x) => x.rows.length);
  const others = results.filter((r) => !ANALYTE_BY_KEY.has(r.analyte)).map(toRow);

  const pd = previous?.derived;
  const derivedRows: Row[] = leaf ? [] : [
    { key: 'ecec', label: derived.ecec_source === 'sum' ? 'ECEC (sum of cations)' : 'ECEC', v: derived.ecec, b: pd?.ecec, unit: 'cmol(+)/kg' },
    { key: 'ca_mg', label: 'Ca:Mg ratio', v: derived.ca_mg_ratio, b: pd?.ca_mg_ratio, unit: null },
    { key: 'esp', label: 'Exchangeable sodium (ESP)', v: derived.esp, b: pd?.esp, unit: '% of ECEC', guide: DERIVED_GUIDE.esp },
    { key: 'al', label: 'Aluminium saturation', v: derived.al_saturation, b: pd?.al_saturation, unit: '% of ECEC', guide: DERIVED_GUIDE.al_saturation },
    { key: 'ca_pct', label: 'Calcium share', v: derived.ca_pct, b: pd?.ca_pct, unit: '% of ECEC' },
    { key: 'mg_pct', label: 'Magnesium share', v: derived.mg_pct, b: pd?.mg_pct, unit: '% of ECEC' },
    { key: 'k_pct', label: 'Potassium share', v: derived.k_pct, b: pd?.k_pct, unit: '% of ECEC' },
  ]
    .filter((d) => d.v != null)
    .map((d) => ({
      key: d.key, label: d.label, value: fmtNum(d.v), unit: d.unit, numeric: d.v as number,
      before: d.b ?? null, beforeText: d.b != null ? fmtNum(d.b) : null,
      verdict: guideVerdict('guide' in d ? d.guide : undefined, d.v), method: null,
    }));

  const showPrevious = !!previous;
  const anyVerdict = [...groups.flatMap((g) => g.rows), ...derivedRows].some((r) => r.verdict);

  return (
    <div className="space-y-4">
      {groups.map(({ g, rows }) => (
        <section key={g}>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">{GROUP_LABEL[g]}</h4>
          <Rows rows={rows} showPrevious={showPrevious} against={against} />
        </section>
      ))}
      {others.length > 0 && (
        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Other results</h4>
          <Rows rows={others} showPrevious={showPrevious} against={against} />
        </section>
      )}
      {derivedRows.length > 0 && (
        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Worked out from the results</h4>
          <Rows rows={derivedRows} showPrevious={showPrevious} against={against} />
        </section>
      )}
      {anyVerdict && <p className="text-xs text-neutral-500">{leaf ? LEAF_NOT_ADVICE : NOT_ADVICE}</p>}
    </div>
  );
}
